import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Database,
  Download,
  FileSpreadsheet,
  HardDrive,
  LayoutDashboard,
  List,
  Menu,
  RefreshCw,
  Search,
  Upload,
  X
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const BRAND = {
  primary: 'bg-[#74c476]',
  primaryHover: 'hover:bg-[#5aa85c]',
  secondary: 'bg-[#222222]',
  accent: 'text-[#74c476]',
  light: 'bg-stone-50'
};

const authFetchLocal = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{label}</div>
    <div className="text-2xl font-bold text-stone-900">{value}</div>
    {hint ? <div className="text-sm text-stone-500 mt-1">{hint}</div> : null}
  </div>
);

export default function SpanishParliamentarySpeechesWorkspace({ user, onLogout, onBackToHub, apiUrl }) {
  const [view, setView] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState({ years: [], legislatures: [], speakers: [], parties: [], speechTypes: [] });
  const [rows, setRows] = useState([]);
  const [selectedSpeechId, setSelectedSpeechId] = useState('');
  const [selectedSpeech, setSelectedSpeech] = useState(null);
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const [legislature, setLegislature] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [party, setParty] = useState('');
  const [speechType, setSpeechType] = useState('');
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [moreInfoSaving, setMoreInfoSaving] = useState(false);
  const [moreInfoMsg, setMoreInfoMsg] = useState(null);
  const [speechMetaForm, setSpeechMetaForm] = useState({
    year: '',
    legislature: '',
    legislaturePeriod: '',
    numberOfWords: '',
    speechType: '',
    speaker: '',
    result: '',
    speakers: '',
    resultCode: '',
    party: '',
    government: ''
  });
  const [activeTopicId, setActiveTopicId] = useState('');
  const [msg, setMsg] = useState(null);
  const speechReaderScrollRef = useRef(null);
  const topicsNavRef = useRef(null);
  const topicItemRefs = useRef({});
  const activeTopicLockRef = useRef(null);

  const canImport = user && user.role === 'admin';
  const canEditSpeechMeta = user && (user.role === 'admin' || user.role === 'coder');
  const displayUserName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.fullName || user?.username || 'User';

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliamentary-speeches/dashboard`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load dashboard');
      setDashboard(data);
    } catch (error) {
      console.error(error);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliamentary-speeches/filters`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load filters');
      setFilters({
        years: Array.isArray(data?.years) ? data.years : [],
        legislatures: Array.isArray(data?.legislatures) ? data.legislatures : [],
        speakers: Array.isArray(data?.speakers) ? data.speakers : [],
        parties: Array.isArray(data?.parties) ? data.parties : [],
        speechTypes: Array.isArray(data?.speechTypes) ? data.speechTypes : []
      });
    } catch (error) {
      console.error(error);
      setFilters({ years: [], legislatures: [], speakers: [], parties: [], speechTypes: [] });
    }
  }, [apiUrl]);

  const fetchSpeeches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (year) params.set('year', year);
      if (legislature) params.set('legislature', legislature);
      if (speaker) params.set('speaker', speaker);
      if (party) params.set('party', party);
      if (speechType) params.set('speechType', speechType);
      const query = params.toString();
      const res = await authFetchLocal(`${apiUrl}/spanish-parliamentary-speeches/speeches${query ? `?${query}` : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load speeches');
      const nextRows = Array.isArray(data?.rows) ? data.rows : [];
      setRows(nextRows);
      if (nextRows.length > 0 && (!selectedSpeechId || !nextRows.some((item) => item.speechId === selectedSpeechId))) {
        setSelectedSpeechId(nextRows[0].speechId);
      }
      if (nextRows.length === 0) {
        setSelectedSpeechId('');
        setSelectedSpeech(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setSelectedSpeechId('');
      setSelectedSpeech(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, legislature, party, search, selectedSpeechId, speaker, speechType, year]);

  const fetchSpeechDetail = useCallback(async (speechId) => {
    if (!speechId) {
      setSelectedSpeech(null);
      return;
    }
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliamentary-speeches/speeches/${encodeURIComponent(speechId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load speech detail');
      setSelectedSpeech(data?.speech || null);
    } catch (error) {
      console.error(error);
      setSelectedSpeech(null);
    }
  }, [apiUrl]);

  const updateSpeechMetaField = (field, value) => {
    setSpeechMetaForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSpeechMeta = async () => {
    if (!selectedSpeechId || !canEditSpeechMeta) return;
    setMoreInfoMsg(null);
    setMoreInfoSaving(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliamentary-speeches/speeches/${encodeURIComponent(selectedSpeechId)}/meta`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(speechMetaForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to update speech metadata');

      setMoreInfoMsg({ type: 'success', text: 'Speech metadata updated.' });
      await Promise.all([fetchSpeechDetail(selectedSpeechId), fetchSpeeches(), fetchDashboard(), fetchFilters()]);
    } catch (error) {
      setMoreInfoMsg({ type: 'error', text: error.message || 'Failed to update speech metadata' });
    } finally {
      setMoreInfoSaving(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchFilters();
  }, [fetchDashboard, fetchFilters]);

  useEffect(() => {
    if (view === 'speeches') fetchSpeeches();
  }, [fetchSpeeches, view]);

  useEffect(() => {
    if (view === 'speeches') fetchSpeechDetail(selectedSpeechId);
  }, [fetchSpeechDetail, selectedSpeechId, view]);

  useEffect(() => {
    if (!selectedSpeech) {
      setSpeechMetaForm({
        year: '',
        legislature: '',
        legislaturePeriod: '',
        numberOfWords: '',
        speechType: '',
        speaker: '',
        result: '',
        speakers: '',
        resultCode: '',
        party: '',
        government: ''
      });
      setMoreInfoMsg(null);
      return;
    }

    setSpeechMetaForm({
      year: selectedSpeech.year ?? '',
      legislature: selectedSpeech.legislature ?? '',
      legislaturePeriod: selectedSpeech.legislaturePeriod ?? '',
      numberOfWords: selectedSpeech.numberOfWords ?? '',
      speechType: selectedSpeech.speechType ?? '',
      speaker: selectedSpeech.speaker ?? '',
      result: selectedSpeech.result ?? '',
      speakers: selectedSpeech.speakers ?? '',
      resultCode: selectedSpeech.resultCode ?? '',
      party: selectedSpeech.party ?? '',
      government: selectedSpeech.government ?? ''
    });
    setMoreInfoMsg(null);
  }, [selectedSpeech]);

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setMsg(null);
    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliamentary-speeches/import-xlsx`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Import failed');

      setMsg({
        type: 'success',
        text: `Imported sentences: ${Number(data?.summary?.totalRows || 0)} | Speeches: ${Number(data?.summary?.totalSpeeches || 0)}`
      });

      await Promise.all([fetchDashboard(), fetchFilters()]);
      if (view === 'speeches') await fetchSpeeches();
    } catch (error) {
      setMsg({ type: 'error', text: error.message || 'Import failed' });
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const downloadBlobFromEndpoint = async (endpoint, filename) => {
    const res = await authFetchLocal(`${apiUrl}${endpoint}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || 'Export failed');
    }
    const contentDisposition = res.headers.get('content-disposition') || '';
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    let resolvedFilename = filename;
    if (filenameMatch?.[1]) {
      try {
        resolvedFilename = decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
      } catch {
        resolvedFilename = filenameMatch[1].replace(/"/g, '').trim() || filename;
      }
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = resolvedFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    try {
      await downloadBlobFromEndpoint('/spanish-parliamentary-speeches/export-csv', `spanish_parliamentary_speeches_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      setMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportXlsx = async () => {
    try {
      await downloadBlobFromEndpoint('/spanish-parliamentary-speeches/export-xlsx', `spanish_parliamentary_speeches_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      setMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportOriginal = async () => {
    try {
      await downloadBlobFromEndpoint('/spanish-parliamentary-speeches/export-original', `spanish_parliamentary_speeches_original_${new Date().toISOString().split('T')[0]}.sql`);
    } catch (error) {
      setMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const legislatureChart = useMemo(() => (dashboard?.byLegislature || []).slice(0, 20).map((item) => ({ name: String(item.label), count: Number(item.count || 0) })), [dashboard]);
  const partyChart = useMemo(() => (dashboard?.byParty || []).slice(0, 10).map((item) => ({ name: item.label, count: Number(item.count || 0) })), [dashboard]);
  const topTopicsBySpeaker = useMemo(() => Array.isArray(dashboard?.topTopicsBySpeaker) ? dashboard.topTopicsBySpeaker : [], [dashboard]);

  const speechSegments = useMemo(() => {
    const sentences = Array.isArray(selectedSpeech?.sentences) ? selectedSpeech.sentences : [];
    if (!sentences.length) return [];

    const segments = [];
    let current = null;

    sentences.forEach((sentence) => {
      const segmentKey = `${String(sentence.topic || '').trim()}::${String(sentence.subtopic || '').trim()}::${String(sentence.subcodeLabel || '').trim()}`;
      if (!current || current.segmentKey !== segmentKey) {
        if (current) segments.push(current);
        current = {
          segmentKey,
          topic: String(sentence.topic || '').trim() || 'Not specified',
          subtopic: String(sentence.subtopic || '').trim() || 'Not specified',
          codeLabel: String(sentence.codeLabel || '').trim(),
          subcodeLabel: String(sentence.subcodeLabel || '').trim(),
          startSentence: sentence.sentenceOrder,
          endSentence: sentence.sentenceOrder,
          textParts: [String(sentence.sentenceText || '').trim()]
        };
      } else {
        current.endSentence = sentence.sentenceOrder;
        current.textParts.push(String(sentence.sentenceText || '').trim());
      }
    });

    if (current) segments.push(current);
    return segments.map((segment) => ({
      ...segment,
      text: segment.textParts.filter(Boolean).join(' ')
    }));
  }, [selectedSpeech]);

  const speechReadingMeta = useMemo(() => {
    const sentences = Array.isArray(selectedSpeech?.sentences) ? selectedSpeech.sentences : [];
    const wordCount = sentences.reduce((acc, sentence) => {
      const text = String(sentence.sentenceText || '').trim();
      if (!text) return acc;
      return acc + text.split(/\s+/).filter(Boolean).length;
    }, 0);

    return {
      wordCount,
      estimatedMinutes: Math.max(1, Math.round(wordCount / 180)),
      topicsCount: speechSegments.length
    };
  }, [selectedSpeech, speechSegments]);

  const topicAnchors = useMemo(() => speechSegments.map((segment, index) => ({
    id: `topic-${index + 1}`,
    topic: segment.topic,
    subtopic: segment.subtopic,
    codeLabel: segment.codeLabel,
    startSentence: segment.startSentence,
    endSentence: segment.endSentence,
    subcodeLabel: segment.subcodeLabel
  })), [speechSegments]);

  const sentenceTopicMap = useMemo(() => {
    const map = {};
    topicAnchors.forEach((topic, index) => {
      for (let sentenceOrder = Number(topic.startSentence || 0); sentenceOrder <= Number(topic.endSentence || 0); sentenceOrder += 1) {
        map[sentenceOrder] = {
          id: topic.id,
          topicIndex: index
        };
      }
    });
    return map;
  }, [topicAnchors]);

  useEffect(() => {
    setActiveTopicId(topicAnchors[0]?.id || '');
  }, [selectedSpeechId, topicAnchors]);

  useEffect(() => {
    const container = speechReaderScrollRef.current;
    if (!container || topicAnchors.length === 0) return;

    const topicsByStart = topicAnchors
      .map((topic) => ({ id: topic.id, startSentence: Number(topic.startSentence || 0) }))
      .filter((topic) => topic.startSentence > 0)
      .sort((a, b) => a.startSentence - b.startSentence);

    if (topicsByStart.length === 0) return;

    const updateActiveTopicFromScroll = () => {
      if (activeTopicLockRef.current && Date.now() < activeTopicLockRef.current) return;
      const sentenceNodes = Array.from(container.querySelectorAll('[data-sentence-order]'));
      if (!sentenceNodes.length) return;

      const thresholdTop = container.getBoundingClientRect().top + 190;
      let currentSentenceOrder = Number(sentenceNodes[0].getAttribute('data-sentence-order') || 0);

      for (const node of sentenceNodes) {
        const rect = node.getBoundingClientRect();
        const sentenceOrder = Number(node.getAttribute('data-sentence-order') || 0);
        if (rect.top <= thresholdTop) {
          currentSentenceOrder = sentenceOrder;
        } else {
          break;
        }
      }

      let nextActiveTopicId = topicsByStart[0].id;
      for (const topic of topicsByStart) {
        if (currentSentenceOrder >= topic.startSentence) {
          nextActiveTopicId = topic.id;
        } else {
          break;
        }
      }

      setActiveTopicId((prev) => (prev === nextActiveTopicId ? prev : nextActiveTopicId));
    };

    updateActiveTopicFromScroll();
    container.addEventListener('scroll', updateActiveTopicFromScroll, { passive: true });
    window.addEventListener('resize', updateActiveTopicFromScroll);

    return () => {
      container.removeEventListener('scroll', updateActiveTopicFromScroll);
      window.removeEventListener('resize', updateActiveTopicFromScroll);
    };
  }, [topicAnchors, selectedSpeechId, readOnlyMode]);

  const handleTopicClick = useCallback((event, topic) => {
    event.preventDefault();
    const container = speechReaderScrollRef.current;
    const targetNode = container?.querySelector(`#sentence-${topic.startSentence}`);

    setActiveTopicId(topic.id);
    activeTopicLockRef.current = Date.now() + 420;

    if (!container || !targetNode) return;
    const targetTop = targetNode.offsetTop - 170;
    container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!activeTopicId) return;
    const nav = topicsNavRef.current;
    const activeItem = topicItemRefs.current[activeTopicId];
    if (!nav || !activeItem) return;

    const navTop = nav.scrollTop;
    const navBottom = navTop + nav.clientHeight;
    const itemTop = activeItem.offsetTop;
    const itemBottom = itemTop + activeItem.offsetHeight;

    if (itemTop < navTop || itemBottom > navBottom) {
      nav.scrollTo({
        top: Math.max(0, itemTop - nav.clientHeight / 2 + activeItem.offsetHeight / 2),
        behavior: 'smooth'
      });
    }
  }, [activeTopicId]);

  return (
    <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
      {loading && !dashboard && (
        <div className="fixed inset-0 z-[70] bg-stone-50 flex items-center justify-center" role="status" aria-live="polite">
          <div className="flex flex-col items-center text-center px-6">
            <RefreshCw className="animate-spin text-green-600 mb-4" size={38} />
            <p className="text-lg font-bold text-stone-800">Loading database…</p>
            <p className="mt-1 text-sm text-stone-500">Preparing visualizations and filters</p>
          </div>
        </div>
      )}
      <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
        <div className="flex flex-col">
          <h1 className="text-lg font-bold tracking-tight">Spanish Parliamentary Speeches</h1>
          <p className="text-xs text-stone-300">Data workspace</p>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-1">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileMenuOpen(false)} />}

      <aside className={`fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="p-6 border-b border-stone-700 hidden md:block">
          <h1 className="text-xl font-sans font-bold tracking-tight">Spanish Parliament<span className={BRAND.accent}> Speeches</span></h1>
          <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Sentence-level dataset</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <div className="px-4 py-2 mb-4">
            <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
            <div className="font-medium text-white truncate">{displayUserName}</div>
            <div className="flex justify-between items-center text-xs mt-1">
              <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user?.role}</span>
              <button onClick={onLogout} title="Log Out">
                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          <button onClick={() => { setView('dashboard'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'dashboard' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </button>

          <button onClick={() => { setView('speeches'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'speeches' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
            <List size={18} />
            <span>Speeches</span>
          </button>

          <div className="pt-4 mt-4 border-t border-stone-700 space-y-1">
            {user?.role !== 'viewer' && <button onClick={() => { setView('data'); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'data' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}>
              <HardDrive size={18} />
              <span>Data Management</span>
            </button>}
            {user?.role !== 'viewer' && <button onClick={() => { onBackToHub(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium text-stone-300 hover:bg-stone-700">
              <Database size={18} />
              <span>Databases Hub</span>
            </button>}
          </div>
        </nav>
      </aside>

      <main className={`flex-1 overflow-auto ${BRAND.light} relative p-5 md:p-8`}>
        <div className="max-w-7xl mx-auto">
          {msg && (
            <div className={`mb-4 p-3 rounded-sm text-sm ${msg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
              {msg.text}
            </div>
          )}

          {view === 'dashboard' && (
            <>
              <div className="mb-6 border-b border-stone-300 pb-4 flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Spanish Parliamentary Speeches</h2>
                </div>
                <button type="button" onClick={fetchDashboard} className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-stone-300 bg-white text-stone-700 hover:bg-stone-100 text-sm">
                  <RefreshCw size={16} /> Refresh
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
                <StatCard label="Sentences" value={Number(dashboard?.summary?.totalSentences || 0).toLocaleString()} />
                <StatCard label="Speeches" value={Number(dashboard?.summary?.totalSpeeches || 0).toLocaleString()} />
                <StatCard label="Speakers" value={Number(dashboard?.summary?.distinctSpeakers || 0).toLocaleString()} />
                <StatCard label="Data Range" value={`${dashboard?.summary?.minYear || '—'} - ${dashboard?.summary?.maxYear || '—'}`} />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                  <h3 className="text-sm font-bold mb-3">Speeches by Legislature</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={legislatureChart} margin={{ top: 10, right: 20, left: 0, bottom: 45 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#74c476" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
                  <h3 className="text-sm font-bold mb-3">Top Parties</h3>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={partyChart} margin={{ top: 10, right: 20, left: 0, bottom: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={90} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#4E647D" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mt-6 bg-white border border-stone-200 rounded-sm shadow-sm p-4">
                <h3 className="text-base font-bold text-stone-900 mb-4">Most used topics by president</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {topTopicsBySpeaker.map((speakerBlock) => (
                    <div key={speakerBlock.speaker} className="border border-stone-200 p-3 bg-stone-50">
                      <div className="text-sm font-bold text-stone-900">{speakerBlock.speaker}</div>
                      <div className="text-xs text-stone-500 mb-3">{Number(speakerBlock.totalSentences || 0).toLocaleString()} coded sentences</div>
                      <div className="space-y-2">
                        {(speakerBlock.topics || []).map((topic) => (
                          <div key={`${speakerBlock.speaker}-${topic.label}`}>
                            <div className="flex items-center justify-between text-xs text-stone-700">
                              <span className="truncate pr-2">{topic.label}</span>
                              <span className="font-semibold">{topic.share}%</span>
                            </div>
                            <div className="h-2 bg-stone-200 mt-1">
                              <div className="h-full bg-[#74c476]" style={{ width: `${Math.max(2, Math.min(100, Number(topic.share || 0)))}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {topTopicsBySpeaker.length === 0 ? <div className="text-sm text-stone-400">No topic data available.</div> : null}
                </div>
              </div>
            </>
          )}

          {view === 'speeches' && (
            <div className="space-y-4">
              <div className="border-b border-stone-300 pb-4 flex items-center justify-between gap-3">
                <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Speech Reader</h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMoreInfoOpen((prev) => !prev)}
                    className={`px-3 py-2 border text-sm ${moreInfoOpen ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'}`}
                  >
                    {moreInfoOpen ? 'Hide More info' : 'More info'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReadOnlyMode((prev) => !prev)}
                    className={`px-3 py-2 border text-sm ${readOnlyMode ? 'bg-[#222222] text-white border-[#222222]' : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'}`}
                  >
                    {readOnlyMode ? 'Exit Read-only Mode' : 'Read-only Mode'}
                  </button>
                </div>
              </div>

              {!readOnlyMode && (
              <div className="bg-white border border-stone-200 p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-2">
                <div className="relative xl:col-span-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search speaker, party, type or text..." className="w-full border border-stone-300 p-2 pl-9 text-sm" />
                </div>
                <select value={legislature} onChange={(e) => setLegislature(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All legislatures</option>{filters.legislatures.map((value) => <option key={`legislature-${value}`} value={value}>{value}</option>)}</select>
                <select value={year} onChange={(e) => setYear(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All years</option>{filters.years.map((value) => <option key={`year-${value}`} value={value}>{value}</option>)}</select>
                <select value={speaker} onChange={(e) => setSpeaker(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All speakers</option>{filters.speakers.map((value) => <option key={`speaker-${value}`} value={value}>{value}</option>)}</select>
                <select value={party} onChange={(e) => setParty(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All parties</option>{filters.parties.map((value) => <option key={`party-${value}`} value={value}>{value}</option>)}</select>
                <select value={speechType} onChange={(e) => setSpeechType(e.target.value)} className="border border-stone-300 p-2 text-sm"><option value="">All types</option>{filters.speechTypes.map((value) => <option key={`stype-${value}`} value={value}>{value}</option>)}</select>
                <button onClick={fetchSpeeches} className="border border-stone-300 px-3 py-2 bg-stone-50 text-sm hover:bg-stone-100">Apply filters</button>
              </div>
              )}

              <div className={`grid gap-4 ${readOnlyMode ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-3'}`}>
                {!readOnlyMode && (
                <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-auto max-h-[72vh]">
                  <div className="px-4 py-3 border-b border-stone-200 text-sm text-stone-600">Visible speeches: <span className="font-bold text-stone-900">{rows.length}</span>{loading ? <span className="ml-2 text-stone-400">Loading...</span> : null}</div>
                  <div className="divide-y divide-stone-100">
                    {rows.map((row) => (
                      <button key={row.speechId} onClick={() => setSelectedSpeechId(row.speechId)} className={`w-full text-left px-4 py-3 hover:bg-stone-50 ${selectedSpeechId === row.speechId ? 'bg-green-50' : ''}`}>
                        <div className="text-sm font-bold text-stone-800">{row.speaker || 'Unknown speaker'}</div>
                        <div className="text-xs text-stone-500 mt-1">{row.legislature || '—'} · {row.year || '—'} · {row.party || '—'} · {row.speechType || '—'}</div>
                        <div className="text-xs text-stone-400 mt-2 line-clamp-2">{row.preview || '—'}</div>
                      </button>
                    ))}
                    {rows.length === 0 && <div className="p-6 text-sm text-stone-400">No speeches found.</div>}
                  </div>
                </div>
                )}

                <div ref={speechReaderScrollRef} className={`${readOnlyMode ? '' : 'xl:col-span-2'} bg-white border border-stone-200 rounded-sm shadow-sm overflow-auto max-h-[72vh]`}>
                  {!selectedSpeech ? (
                    <div className="p-8 text-stone-400">Select a speech to read it.</div>
                  ) : (
                    <div className="p-0 relative">
                      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-stone-200 px-5 py-4">
                        <h3 className="text-xl font-bold text-stone-900">{selectedSpeech.speaker}</h3>
                        <div className="text-sm text-stone-600 mt-1">{selectedSpeech.legislature || '—'} · {selectedSpeech.year || '—'} · {selectedSpeech.party || '—'} · {selectedSpeech.speechType || '—'}</div>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div className="bg-stone-100 border border-stone-200 px-2 py-1 text-stone-700"><span className="font-semibold text-stone-900">{selectedSpeech.sentenceCount}</span> sentences</div>
                          <div className="bg-stone-100 border border-stone-200 px-2 py-1 text-stone-700"><span className="font-semibold text-stone-900">{speechReadingMeta.wordCount.toLocaleString()}</span> words</div>
                          <div className="bg-stone-100 border border-stone-200 px-2 py-1 text-stone-700"><span className="font-semibold text-stone-900">~{speechReadingMeta.estimatedMinutes} min</span> read</div>
                          <div className="bg-stone-100 border border-stone-200 px-2 py-1 text-stone-700"><span className="font-semibold text-stone-900">{speechReadingMeta.topicsCount}</span> topics</div>
                        </div>
                      </div>

                      {moreInfoOpen && (
                        <section className="px-5 py-4 border-b border-stone-200 bg-stone-50">
                          <h4 className="font-bold text-stone-900 mb-3">More info</h4>
                          <div className="space-y-3 text-sm">
                            {moreInfoMsg ? (
                              <div className={`p-2 border ${moreInfoMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
                                {moreInfoMsg.text}
                              </div>
                            ) : null}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Year</label>
                                <input type="number" value={speechMetaForm.year} onChange={(e) => updateSpeechMetaField('year', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Legislature</label>
                                <input value={speechMetaForm.legislature} onChange={(e) => updateSpeechMetaField('legislature', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                            </div>

                            <div>
                              <label className="text-xs font-bold uppercase text-stone-500">Legislature period</label>
                              <input value={speechMetaForm.legislaturePeriod} onChange={(e) => updateSpeechMetaField('legislaturePeriod', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Speech type</label>
                                <input value={speechMetaForm.speechType} onChange={(e) => updateSpeechMetaField('speechType', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Speaker</label>
                                <input value={speechMetaForm.speaker} onChange={(e) => updateSpeechMetaField('speaker', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Party</label>
                                <input value={speechMetaForm.party} onChange={(e) => updateSpeechMetaField('party', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Government</label>
                                <input value={speechMetaForm.government} onChange={(e) => updateSpeechMetaField('government', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Result</label>
                                <input value={speechMetaForm.result} onChange={(e) => updateSpeechMetaField('result', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                              <div>
                                <label className="text-xs font-bold uppercase text-stone-500">Speakers (count)</label>
                                <input type="number" value={speechMetaForm.speakers} onChange={(e) => updateSpeechMetaField('speakers', e.target.value)} disabled={!canEditSpeechMeta} className="w-full border border-stone-300 px-2 py-1 mt-1 bg-white" />
                              </div>
                            </div>

                            <div className="pt-3 border-t border-stone-200 text-xs text-stone-500 space-y-1">
                              <div><span className="font-semibold text-stone-700">Source sheet:</span> {selectedSpeech.sourceSheet || '—'}</div>
                              <div><span className="font-semibold text-stone-700">Imported at:</span> {selectedSpeech.importedAt || '—'}</div>
                            </div>

                            {canEditSpeechMeta ? (
                              <button type="button" onClick={handleSaveSpeechMeta} disabled={moreInfoSaving} className="w-full md:w-auto border border-stone-800 bg-stone-800 text-white px-4 py-2 hover:bg-stone-700 disabled:opacity-60">
                                {moreInfoSaving ? 'Saving...' : 'Save for full speech'}
                              </button>
                            ) : (
                              <div className="text-xs text-stone-500">You can view this metadata, but only admin/coder can edit it.</div>
                            )}
                          </div>
                        </section>
                      )}

                      <div className="px-5 py-6 md:px-8 md:py-8">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                          <article className="lg:col-span-8 max-w-4xl text-[17px] leading-8 text-stone-800 text-justify">
                            {(selectedSpeech.sentences || []).map((sentence) => {
                              const sentenceId = `sentence-${sentence.sentenceOrder}`;
                              const text = String(sentence.sentenceText || '').trim();
                              if (!text) return null;
                              const sentenceTopic = sentenceTopicMap[Number(sentence.sentenceOrder || 0)];
                              const isActiveSentence = sentenceTopic?.id && sentenceTopic.id === activeTopicId;
                              const stripeClasses = ['bg-emerald-50/75', 'bg-sky-50/75', 'bg-amber-50/75', 'bg-rose-50/75', 'bg-violet-50/75'];
                              const sentenceToneClass = sentenceTopic ? stripeClasses[sentenceTopic.topicIndex % stripeClasses.length] : 'bg-stone-50';
                              const activeToneClass = 'bg-yellow-300 ring-1 ring-yellow-500';
                              return (
                                <span
                                  key={sentenceId}
                                  id={sentenceId}
                                  data-sentence-order={sentence.sentenceOrder}
                                  className={`scroll-mt-48 inline rounded px-1 ${isActiveSentence ? activeToneClass : sentenceToneClass}`}
                                >
                                  {text}{' '}
                                </span>
                              );
                            })}
                          </article>

                          <aside ref={topicsNavRef} className="lg:col-span-4 lg:sticky lg:top-44 self-start border border-stone-200 bg-stone-50 p-4 max-h-[60vh] overflow-auto">
                            <div className="text-[11px] uppercase tracking-wide font-bold text-stone-500 mb-3">Topics linked to speech text</div>
                            <div className="space-y-2">
                              {topicAnchors.map((topic) => (
                                <a
                                  key={topic.id}
                                  href={`#sentence-${topic.startSentence}`}
                                  ref={(node) => {
                                    if (!node) return;
                                    topicItemRefs.current[topic.id] = node;
                                  }}
                                  onClick={(event) => handleTopicClick(event, topic)}
                                  className={`block p-2 border ${activeTopicId === topic.id ? 'border-yellow-500 bg-yellow-300' : 'border-stone-200 bg-white hover:bg-stone-100'}`}
                                >
                                  <div className="text-sm font-semibold text-stone-800">{topic.topic}</div>
                                  <div className="text-xs text-stone-600 mt-1">CAP code: {topic.codeLabel || '—'}</div>
                                </a>
                              ))}
                            </div>
                          </aside>
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {view === 'data' && user?.role !== 'viewer' && (
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
                <h2 className="text-3xl font-bold text-stone-900">Data Management</h2>
                <button onClick={fetchDashboard} className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50">
                  <RefreshCw size={14} /> Refresh status
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {canImport && (
                  <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-green-100 text-green-800 rounded-full"><Upload size={24} /></div>
                      <div>
                        <h3 className="text-xl font-bold text-stone-800">Import XLSX</h3>
                        <p className="text-sm text-stone-500">Upload and replace the active speeches workbook.</p>
                      </div>
                    </div>
                    <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:bg-stone-50 transition-colors relative">
                      <input type="file" accept=".xlsx" onChange={handleImport} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      <div className="text-stone-400 flex flex-col items-center">
                        {loading ? <RefreshCw className="animate-spin mb-2" /> : <Upload className="mb-2" size={32} />}
                        <span className="font-medium text-stone-600">Click to upload XLSX file</span>
                        <span className="text-xs mt-1">Expected source: Speeches1982_2025.xlsx</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className={`bg-white p-8 rounded-sm shadow-sm border border-stone-200 ${canImport ? '' : 'lg:col-span-2'}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-stone-100 text-stone-800 rounded-full"><Database size={24} /></div>
                    <div>
                      <h3 className="text-xl font-bold text-stone-800">Export / Source</h3>
                      <p className="text-sm text-stone-500">Download data in CSV, Excel or original workbook format.</p>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm text-stone-700">
                    <button onClick={handleExportCsv} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                      <div className="flex items-center gap-3"><FileSpreadsheet className="text-green-600" size={20} /><div className="text-left"><div className="font-bold text-stone-800 group-hover:text-green-800">Export full CSV</div><div className="text-xs text-stone-500">All sentence-level rows</div></div></div>
                      <Download size={18} />
                    </button>
                    <button onClick={handleExportXlsx} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                      <div className="flex items-center gap-3"><FileSpreadsheet className="text-green-600" size={20} /><div className="text-left"><div className="font-bold text-stone-800 group-hover:text-green-800">Export full Excel (.xlsx)</div><div className="text-xs text-stone-500">Workbook with sentence-level rows</div></div></div>
                      <Download size={18} />
                    </button>
                    <button onClick={handleExportOriginal} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                      <div className="flex items-center gap-3"><HardDrive className="text-green-600" size={20} /><div className="text-left"><div className="font-bold text-stone-800 group-hover:text-green-800">Download full DB in original format</div><div className="text-xs text-stone-500">Current storage engine snapshot (.db SQLite / .sql PostgreSQL)</div></div></div>
                      <Download size={18} />
                    </button>
                    <div><div className="text-xs font-bold uppercase text-stone-400 mb-1">Storage engine</div><div className="capitalize">{dashboard?.storageEngine || 'sqlite'}</div></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
